import React, { lazy, Suspense, useEffect, useState } from 'react'

interface ReCaptchaProviderProps {
  reCaptchaKey?: string
  triggerId?: string
}

const LazyReCaptchaProviderImpl = lazy(() => import('./ReCaptchaProvider'))

const isTriggerEvent = (event: Event, triggerId: string) => {
  const target = event.target
  if (!(target instanceof Element)) return false
  return Boolean(target.closest(`#${triggerId}`))
}

const ReCaptchaProvider: React.FC<React.PropsWithChildren<ReCaptchaProviderProps>> = ({
  reCaptchaKey,
  triggerId,
  children,
}) => {
  const [enabled, setEnabled] = useState(!triggerId)

  useEffect(() => {
    if (enabled || !triggerId) return

    const handleTriggerEvent = (event: Event) => {
      if (isTriggerEvent(event, triggerId)) setEnabled(true)
    }

    document.addEventListener('pointerdown', handleTriggerEvent, { capture: true, passive: true })
    document.addEventListener('focusin', handleTriggerEvent, true)
    document.addEventListener('keydown', handleTriggerEvent, true)
    document.addEventListener('submit', handleTriggerEvent, true)

    return () => {
      document.removeEventListener('pointerdown', handleTriggerEvent, true)
      document.removeEventListener('focusin', handleTriggerEvent, true)
      document.removeEventListener('keydown', handleTriggerEvent, true)
      document.removeEventListener('submit', handleTriggerEvent, true)
    }
  }, [enabled, triggerId])

  if (!reCaptchaKey || !enabled) return children

  return (
    <Suspense fallback={null}>
      <LazyReCaptchaProviderImpl reCaptchaKey={reCaptchaKey}>{children}</LazyReCaptchaProviderImpl>
    </Suspense>
  )
}

export default ReCaptchaProvider
